import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { fetchPrefs, updatePrefs } from "@/lib/api";
import { cn } from "@/lib/utils";
import { NAME_SWATCHES, COLOR_KEYS } from "@/lib/colors";

/** A small accessible on/off switch. */
function Toggle({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50",
        checked ? "bg-brand-600" : "border border-border bg-elevated",
      )}
    >
      <span
        className={cn(
          "inline-block size-4 transform rounded-full bg-white shadow transition-transform",
          checked ? "translate-x-4" : "translate-x-0.5",
        )}
      />
    </button>
  );
}

/** Personal settings, tied to the signed-in account. */
export function SettingsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const [greet, setGreet] = useState(true);
  const [name, setName] = useState("");
  const [savedName, setSavedName] = useState("");
  const [color, setColor] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoaded(false);
    const ac = new AbortController();
    fetchPrefs(ac.signal)
      .then((p) => {
        setGreet(p.greet !== false);
        setName(p.name || "");
        setSavedName(p.name || "");
        setColor(p.color || "");
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
    return () => ac.abort();
  }, [open]);

  async function save(patch: { greet?: boolean; name?: string; color?: string }) {
    setSaving(true);
    try {
      await updatePrefs(patch);
      return true;
    } catch {
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function setGreetPref(next: boolean) {
    const prev = greet;
    setGreet(next);
    if (!(await save({ greet: next }))) setGreet(prev);
  }

  async function saveName() {
    const next = name.trim().slice(0, 40);
    if (next === savedName) return;
    if (await save({ name: next })) setSavedName(next);
  }

  async function setColorPref(next: string) {
    const prev = color;
    const value = next === color ? "" : next; // click the active swatch to clear (auto)
    setColor(value);
    if (!(await save({ color: value }))) setColor(prev);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Settings</DialogTitle>
          <DialogDescription>Personal preferences, tied to your CodeTogether sign-in.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-4 py-4">
          <div className="min-w-0">
            <p className="text-sm font-medium">Let Max greet me when I join a room</p>
            <p className="mt-0.5 text-xs text-muted">
              Max’s proactive welcome when you’re first into a room. Doesn’t affect saying “Hey Max”.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {saving ? <Loader2 className="size-3 animate-spin text-muted" /> : null}
            <Toggle
              checked={greet}
              disabled={!loaded || saving}
              onChange={setGreetPref}
              label="Let Max greet me when I join a room"
            />
          </div>
        </div>

        <div className="border-t border-border py-4">
          <label htmlFor="pref-name" className="text-sm font-medium">
            Display name
          </label>
          <p className="mb-2 mt-0.5 text-xs text-muted">
            How you appear in rooms and the transcript. Leave blank to use your CodeTogether name.
          </p>
          <input
            id="pref-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => void saveName()}
            onKeyDown={(e) => {
              if (e.key === "Enter") void saveName();
            }}
            disabled={!loaded || saving}
            maxLength={40}
            placeholder="Your name"
            className="w-full rounded-md border border-border bg-bg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-brand-600 disabled:opacity-50"
          />
        </div>

        <div className="border-t border-border py-4">
          <p className="text-sm font-medium">Name color</p>
          <p className="mb-2 mt-0.5 text-xs text-muted">
            Your name’s color in the transcript. Click the selected one again for automatic.
          </p>
          <div className="flex flex-wrap gap-2">
            {COLOR_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                aria-label={key}
                aria-pressed={color === key}
                disabled={!loaded || saving}
                onClick={() => void setColorPref(key)}
                className={cn(
                  "size-6 rounded-full ring-offset-2 ring-offset-surface transition-transform hover:scale-110 disabled:opacity-50",
                  NAME_SWATCHES[key],
                  color === key ? "ring-2 ring-brand-600" : "",
                )}
              />
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
